import type { Envelope, EnvelopeMeta, LoginResponse } from '@diora/contracts';
import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { i18next } from '@/i18n';

export const API_BASE = '/api/v1';

/**
 * An error a caller can branch on.
 *
 * `code` is the stable contract; `message` from the server is already
 * translated prose and is deliberately NOT carried here, so nothing downstream
 * is tempted to parse it.
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly fieldErrors?: Record<string, string[]>,
    readonly requestId?: string,
  ) {
    super(code);
    this.name = 'ApiError';
  }

  /** The stable codes for one field, or an empty list. */
  fieldCodes(field: string): string[] {
    return this.fieldErrors?.[field] ?? [];
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

// ------------------------------------------------------------ access token

/**
 * The access token lives in memory and nowhere else.
 *
 * localStorage survives the tab, is readable by any script that gets injected,
 * and cannot be revoked. The refresh cookie is HttpOnly and carries the long
 * life; this one is short and disposable, and a page reload simply refreshes it.
 */
let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;
let onPasswordChangeRequired: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called when the refresh cookie is gone or rejected and the session is over. */
export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

/**
 * Called when the API says this account must change its password first.
 *
 * Not the same as a lost session, and the difference is not academic: an Owner
 * can reset a colleague's password while that colleague has the dashboard open.
 * Signing them out would be wrong — the session is fine — and showing a generic
 * error would leave them stuck, because the only thing that clears it is the
 * screen they are not being sent to.
 */
export function setPasswordChangeRequiredHandler(handler: () => void): void {
  onPasswordChangeRequired = handler;
}

export const http: AxiosInstance = axios.create({
  baseURL: API_BASE,
  // The refresh cookie is HttpOnly and SameSite=Strict; without this it is
  // never sent and every refresh fails as if the session had expired.
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  // The server resolves the locale from this and answers with a translated
  // `message`. We ignore that message, but validation error ordering and any
  // server-rendered text follow it, so it stays honest.
  config.headers.set('Accept-Language', i18next.language || 'ar');
  return config;
});

// ---------------------------------------------------------------- refresh

/**
 * One refresh at a time.
 *
 * Rotation revokes the token it replaces, and the API treats a replay of a
 * revoked token as theft and kills the whole family. Two concurrent 401s each
 * firing their own refresh is exactly that replay — so they share one promise
 * and the loser waits for the winner.
 */
let refreshSessionInFlight: Promise<LoginResponse> | null = null;

const AUTH_PATHS = ['/admin/auth/login', '/admin/auth/refresh', '/admin/auth/logout'];

function isAuthPath(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_PATHS.some((path) => url.includes(path));
}

/**
 * Rotate the refresh cookie and get a new access token.
 *
 * Exported because the session bootstrap needs it too. Both callers MUST come
 * through here: React's StrictMode runs an effect twice in development, and two
 * independent refresh calls are indistinguishable from a stolen cookie being
 * replayed — the server revokes the whole family and signs the person out for
 * no reason. One promise, shared, means the second caller waits for the first.
 */
export async function refreshSession(): Promise<LoginResponse> {
  if (!refreshSessionInFlight) {
    refreshSessionInFlight = axios
      .post<Envelope<LoginResponse>>(
        `${API_BASE}/admin/auth/refresh`,
        {},
        { withCredentials: true },
      )
      .then((response) => {
        setAccessToken(response.data.data.accessToken);
        return response.data.data;
      })
      .catch((error: AxiosError<Envelope<unknown>>) => {
        /*
         * This is the one call that bypasses `http`, so it also bypasses the
         * response interceptor that turns every other failure into an ApiError.
         *
         * Without this, a failed refresh escapes as a raw AxiosError, and its
         * `message` is untranslated English from a library. That is exactly what
         * reached the screen: "Request failed with status code 401", rendered in
         * Latin script in the middle of an Arabic form.
         */
        throw toApiError(error);
      })
      .finally(() => {
        refreshSessionInFlight = null;
      });
  }
  return refreshSessionInFlight;
}

async function refreshAccessToken(): Promise<string> {
  const session = await refreshSession();
  return session.accessToken;
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<Envelope<unknown>>) => {
    const config = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status ?? 0;

    if (status === 401 && config && !config._retried && !isAuthPath(config.url)) {
      config._retried = true;

      /*
       * Only the refresh is guarded here.
       *
       * The retry used to sit inside this same `try`, so a retried request that
       * came back 401 was caught by the handler for "the refresh failed" and
       * signed the person out — with a perfectly valid session. It is not a
       * hypothetical: an account whose password is reset while the dashboard is
       * open refreshes fine and 401s on every route, and the dashboard threw the
       * user at the login screen instead of the one screen that clears it.
       *
       * Left outside, the retry's own failure runs the interceptor again with
       * `_retried` already set and is reported for what it is.
       */
      try {
        await refreshAccessToken();
      } catch {
        setAccessToken(null);
        onSessionLost?.();
        throw toApiError(error);
      }

      return await http.request(config);
    }

    const apiError = toApiError(error);

    // The session is valid; it is the password that is temporary. Refreshing
    // succeeds and every route still 401s, so this is the one 401 that has to
    // change where the user is rather than whether they are signed in.
    if (apiError.code === 'PASSWORD_CHANGE_REQUIRED') onPasswordChangeRequired?.();

    throw apiError;
  },
);

function toApiError(error: AxiosError<Envelope<unknown>>): ApiError {
  const status = error.response?.status ?? 0;
  const body = error.response?.data;

  if (body?.error) {
    return new ApiError(body.error.code, status, body.error.fieldErrors, body.error.requestId);
  }

  // No envelope means the request never reached the API — a dropped connection,
  // a dead dev server, CORS. Worth telling apart from a server that answered.
  if (status === 0) return new ApiError('NETWORK_UNREACHABLE', 0);
  return new ApiError('INTERNAL_ERROR', status);
}

// ------------------------------------------------------------------ verbs

/** The envelope's `data`, with `meta` when the caller asks for it. */
export interface Result<T> {
  data: T;
  meta?: EnvelopeMeta;
}

export async function apiGet<T>(url: string, params?: unknown): Promise<Result<T>> {
  const response = await http.get<Envelope<T>>(url, { params: params as never });
  return { data: response.data.data, meta: response.data.meta };
}

export async function apiPost<T>(url: string, body?: unknown): Promise<Result<T>> {
  const response = await http.post<Envelope<T>>(url, body ?? {});
  return { data: response.data.data, meta: response.data.meta };
}

export async function apiPatch<T>(url: string, body: unknown): Promise<Result<T>> {
  const response = await http.patch<Envelope<T>>(url, body);
  return { data: response.data.data, meta: response.data.meta };
}

export async function apiPut<T>(url: string, body: unknown): Promise<Result<T>> {
  const response = await http.put<Envelope<T>>(url, body);
  return { data: response.data.data, meta: response.data.meta };
}

/**
 * Multipart upload. The Content-Type header is deleted so the browser sets it
 * with the boundary — setting it by hand produces a body the server cannot parse.
 */
export async function apiUpload<T>(url: string, form: FormData): Promise<Result<T>> {
  const response = await http.post<Envelope<T>>(url, form, {
    headers: { 'Content-Type': undefined as unknown as string },
  });
  return { data: response.data.data, meta: response.data.meta };
}

/** There is no DELETE anywhere in this API — archiving is the only removal. */
